import type * as React from 'react';

import * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type { NavigationChangeRequestsSideSheet } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
    SegmentedControl,
    SidePanel,
    SidePanelBody,
    SidePanelCloseButton,
    SidePanelHeader,
    SidePanelHeaderActions,
    SidePanelHeaderMain,
    SidePanelHeaderTitle,
} from '@gitbook/spine-react';

import {
    CHANGE_REQUEST_STATUSES,
    ChangeRequestListItemSkeleton,
    ChangeRequestsListFilterButton,
    type ChangeRequestsListFilterState,
    ChangeRequestsListFiltersSuggestions,
    StandaloneChangeRequestList,
    useCreateChangeRequest,
} from '../../../Components';
import { useSetAnalyticsScreen, useSideSheetNavigation } from '../../../navigation';

/**
 * Sidepanel to display the change requests of a space.
 */
export function ChangeRequestsListSidePanel(props: {
    space: api.Space;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}): React.ReactElement | null {
    const { space, analyticsContext } = props;
    const { close, sideSheet, updateSideSheet } =
        useSideSheetNavigation<NavigationChangeRequestsSideSheet>('change-requests');

    const filters = sideSheet?.filters ?? {};
    const onFiltersChange = (filters: ChangeRequestsListFilterState) => {
        updateSideSheet({ type: 'change-requests', filters });
    };

    useSetAnalyticsScreen(
        { ...analyticsContext, screen: 'space_change_requests', screenTab: filters.status },
        [filters.status]
    );

    return (
        <SidePanel onClose={close}>
            <SidePanelHeader>
                <SidePanelHeaderMain>
                    <SidePanelHeaderTitle>
                        <Heading>Change requests</Heading>
                    </SidePanelHeaderTitle>
                    <SidePanelHeaderActions>
                        <SidePanelCloseButton />
                    </SidePanelHeaderActions>
                </SidePanelHeaderMain>
                <div className="flex flex-col">
                    <div className="border-b px-3 pb-2">
                        <SegmentedControl
                            active={filters.status ?? api.ChangeRequestStatus.Open}
                            options={CHANGE_REQUEST_STATUSES.map((option) => ({
                                label: option.label,
                                value: option.value,
                            }))}
                            onChange={(status) =>
                                onFiltersChange({
                                    ...filters,
                                    status: status,
                                })
                            }
                            size="small"
                        />
                    </div>
                    <div className="flex flex-1 items-center justify-between gap-4 py-2 pr-3 pl-3">
                        <div className="flex gap-2">
                            <ChangeRequestsListFiltersSuggestions
                                filters={filters}
                                onFiltersChange={onFiltersChange}
                            />
                        </div>
                        <ChangeRequestsListFilterButton
                            filters={filters}
                            onFiltersChange={onFiltersChange}
                            withSpaceFilter={false}
                            iconOnly
                        />
                    </div>
                </div>
            </SidePanelHeader>
            <SidePanelBody
                fallback={
                    <div className="flex flex-col">
                        <ChangeRequestListItemSkeleton />
                        <ChangeRequestListItemSkeleton />
                        <ChangeRequestListItemSkeleton />
                    </div>
                }
            >
                <StandaloneChangeRequestList
                    spaceId={space.id}
                    filters={filters}
                    onFiltersChange={onFiltersChange}
                    emptyState={
                        filters?.contributor ||
                        filters?.creator ||
                        filters?.requestedReview ||
                        filters?.conversationsIssuesCluster ||
                        filters?.status === api.ChangeRequestStatus.Archived ||
                        filters?.status === api.ChangeRequestStatus.Merged ? undefined : (
                            <ChangeRequestsStartEmptyState
                                space={space}
                                analyticsContext={analyticsContext}
                            />
                        )
                    }
                />
            </SidePanelBody>
        </SidePanel>
    );
}

/**
 * Empty state to encourage the user to start a change request.
 */
function ChangeRequestsStartEmptyState(props: {
    space: api.Space;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}) {
    const { space } = props;

    const createChangeRequest = useCreateChangeRequest();

    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.ChangeRequest />
            </EmptyStateIcon>
            <Heading>No change requests yet</Heading>
            <Description>Start making changes to your docs with a change request.</Description>
            {space.permissions.edit ? (
                <EmptyStateActions>
                    <Button
                        kind="primary"
                        leadingIcon={Icon.ChangeRequestNew}
                        label="New change request"
                        onPress={async () => {
                            await createChangeRequest({
                                space,
                                source: 'space-empty-state',
                            });
                        }}
                    />
                </EmptyStateActions>
            ) : null}
        </EmptyState>
    );
}
